/* eslint-disable no-multi-assign */
/* eslint-disable no-param-reassign */

import OMT_UI_ScrollBar from './OMT_UI_ScrollBar';
import OMT_UI_ScrollBarConfig from './OMT_UI_ScrollBarConfig';

/**
 * class for windows with scrollable content
 */
export default class OMT_UI_ScrollArea extends Phaser.Sprite {
  /**
   * constructor
   * @param {Phaser.Rectangle} scrollRect Rectangle for scrollable area
   * @param {OMT_UI_ScrollBarConfig} scrollBarConfig Class refernece for the scroll bar. Should extend OMT_UI_ScrollBar.
   */
  constructor(scrollRect, scrollBarConfig = null) {
    super(game);

    if (!scrollBarConfig) scrollBarConfig = new OMT_UI_ScrollBarConfig();

    this._enabled = true;
    this._pointerDown = false;
    this._pointerDownInContent = false;
    this._contentDragStartY = 0;
    this._contentDragVelY = 0;
    this._contentDragFriction = 0.95;
    this._lastContentHeight = 0;
    this._extraHeight = 0;
    this._autoResize = true;

    this._childInputEnabled = true;
    this._childrenWithInput = [];

    // get position from scroll rect
    this.x = scrollRect.x; this.y = scrollRect.y;

    // clone the scroll rect and make relative
    this._scrollRect = scrollRect.clone();
    this._scrollRect.x = this._scrollRect.y = 0;
    this._pointerRect = this._scrollRect.clone();

    // adjust rectangles to compensate for scrollbar overlap / no overlap
    if (!scrollBarConfig.overlapScrollArea) {
      this._scrollRect.width -= scrollBarConfig.width;
    } else {
      this._pointerRect.width -= scrollBarConfig.width;
    }

    this._initContentContainer();
    this._initScrollMask();
    this._initScrollBar(scrollBarConfig);
    this._setPointerMovedCallback();
  }

  /**
   * get a copy of the current scroll rect
   * @returns {Phaser.Rectangle}
   */
  get scrollRect() {
    return this._scrollRect.clone();
  }

  /**
   * get the scrollbar config
   * @returns {OMT_UI_ScrollBarConfig}
   */
  get scrollBarConfig() {
    return this._scrollBar.config;
  }

  /**
   * init the scroll content container
   */
  _initContentContainer() {
    this._content = new Phaser.Group(game);
    this.addChild(this._content);
  }

  /**
   * get a reference to the scroll content group
   * @returns {Phaser.Group}
   */
  get content() {
    return this._content;
  }

  /**
   * get the height of the content ignoring negative portion
   * @param {boolean} update update height values
   * @returns {number}
   */
  _getContentHeight(update = true) {
    if (!update) return this._lastContentHeight;
    const contentMinY = this._content.getBounds(this._content).y;
    const contentHeight = this._extraHeight + (contentMinY >= 0 ? this._content.height : this._content.height + contentMinY);
    return contentHeight;
  }

  /**
   * set some extra height if needed as sometimes Phaser height calculations can be buggy
   * @param {number} value
   */
  set extraHeight(value) {
    this._extraHeight = value;
  }

  /**
   * init / apply the scroll mask
   */
  _initScrollMask() {
    this._scrollMask = new Phaser.Graphics(game);
    this._scrollMask.beginFill(0xff0000);
    this._scrollMask.drawRect(0, 0, this._scrollRect.width, this._scrollRect.height);
    this._scrollMask.endFill();
    this._scrollMask.enabled = false;
    this.addChildAt(this._scrollMask, 0);
    this._content.mask = this._scrollMask;
  }

  /**
   * init the scroll bar
   * @param {OMT_UI_ScrollBarConfig} scrollBarConfig
   */
  _initScrollBar(scrollBarConfig) {
    this._scrollBar = new OMT_UI_ScrollBar(this._scrollRect, scrollBarConfig);
    this.addChild(this._scrollBar);
    this._scrollBar.signals.onScrollChange.add(this._onScrolled.bind(this));
  }

  /**
   * get a reference to the scrollbar
   * @returns {OMT_UI_ScrollBar} returns a reference to the scrollbar
   */
  get scrollBar() {
    return this._scrollBar;
  }

  /**
   * user scrolled
   */
  _onScrolled() {
    const contentHeight = this._getContentHeight();
    const visiblePercent = this._scrollRect.height / contentHeight;
    const scrollHeight = contentHeight * (1 - visiblePercent);
    const scrollPercent = contentHeight > this._scrollRect.height ? this._scrollBar.getScrollPercent() : 0;
    this._content.y = -scrollHeight * scrollPercent;
    this._contentDragVelY = 0;
  }

  /**
   * update called by phaser
   * @param {boolean} updateContent
   */
  update(updateContent = true) {
    super.update();
    if (updateContent) this._content.update();
    const localPointer = this._getLocalPointer();
    const containsPointer = this._enabled && Phaser.Rectangle.containsPoint(this._pointerRect, localPointer)
      && !this._scrollBar.dragActive && !OMT.social.isOverlayVisible;

    this._updateInputState(this._content, containsPointer);
    if (this._autoResize) this.updateScrollBar();
    this._updateContentDragging(containsPointer);
    this._scrollBar.updateEnabledState();
  }

  /**
   * check if the scroll area height changed and adjust if neccessary
   */
  updateScrollBar() {
    const contentHeight = this._getContentHeight(true);
    const scrollHeight = contentHeight - this._scrollRect.height;

    // update scrollbar / knob
    if (this._lastContentHeight !== contentHeight) {
      const viewPercent = this._scrollRect.height / contentHeight;
      const scrollPercent = -this._content.y / scrollHeight;
      this._scrollBar.updateScrollKnobGraphics(viewPercent);
      this._scrollBar.setScrollPercent(scrollPercent, false);
      this._lastContentHeight = contentHeight;
    }
  }

  /**
   * recusively enable / disable input on children.
   * we keep a list of the disabled children as we dont want to enable intentionally disabled things.
   * @param {Object} displayObject
   * @param {boolean} enabled
   * @param {number} depth
   */
  _updateInputState(displayObject, enabled, depth = 0) {
    // set the input state when at depth 0
    if (depth === 0) {
      if (enabled === this._childInputEnabled) return;
      this._childInputEnabled = enabled;
    }

    let child;
    // only enable children that were previously disabled
    if (enabled) {
      for (let i = 0; i < this._childrenWithInput.length; i++) {
        child = this._childrenWithInput[i];
        child.inputEnabled = true;
        if (child.isUsingHandCursor) child.input.useHandCursor = true;
      }
      this._childrenWithInput.length = 0;
    } else { // disable children if they have active input
      for (let i = 0; i < displayObject.children.length; i++) {
        child = displayObject.children[i];
        if (child.input && child.inputEnabled) {
          child.isUsingHandCursor = child.input.useHandCursor;
          child.inputEnabled = false;
          this._childrenWithInput.push(child);
        }
        if (child.children) {
          this._updateInputState(child, enabled, depth + 1);
        }
      }
    }
  }

  /**
   * update content dragging
   * @param {boolean} containsPointer
   */
  _updateContentDragging(containsPointer) {
    const contentHeight = this._getContentHeight(this._autoResize);
    if (contentHeight < this._scrollRect.height) return;

    // capture the pointer down state
    if (!this._pointerDown && game.input.activePointer.isDown) {
      this._pointerDown = true;
      this._pointerDownInContent = containsPointer;
      this._contentDragStartY = game.input.y;
    } else if (this._pointerDown && !game.input.activePointer.isDown) {
      this._pointerDown = false;
      this._pointerDownInContent = false;
    } else if (this._pointerDown && !containsPointer) {
      this._pointerDownInContent = false;
    }

    // update content drag velocity
    if (this._pointerDownInContent && containsPointer) {
      this._contentDragVelY = this._contentDragStartY - game.input.y;
      this._contentDragStartY = game.input.y;
    }

    if (this._contentDragVelY === 0) { return; } // Not moving
    if (this._contentDragVelY > 0 && this._content.y === contentHeight) { this._contentDragVelY = 0; return; } // Clamped to top
    if (this._contentDragVelY < 0 && this._content.y === 0) { this._contentDragVelY = 0; return; } // Clamped to bot

    // scroll as long as we have velocity
    if (Math.abs(this._contentDragVelY) > 0) {
      // update content position
      this._content.y -= this._contentDragVelY;
      this._content.y = Math.max(Math.min(this._content.y, 0), this._scrollRect.height - contentHeight);

      // update scroll bar position
      const scrollPercent = -this._content.y / (contentHeight - this._scrollRect.height);
      this._scrollBar.setScrollPercent(scrollPercent, false);

      // apply friction to drag velocity
      this._contentDragVelY *= this._contentDragFriction;
    }
  }

  /**
   * jump to content position
   * @param {number} yPos
   */
  jumpToContentPosition(yPos) {
    // move content
    const contentHeight = this._getContentHeight(this._autoResize);
    this._contentDragVelY = 0;
    this._content.y = -yPos;
    this._content.y = Math.max(Math.min(this._content.y, 0), this._scrollRect.height - contentHeight);

    // update scroll bar position
    const scrollPercent = -this._content.y / (contentHeight - this._scrollRect.height);
    this._scrollBar.setScrollPercent(scrollPercent, false);
  }

  /**
   * get local pointer position
   * @returns {Phaser.Point}
   */
  _getLocalPointer() {
    const pointer = this.toLocal(this.game.input);
    return pointer;
  }

  /**
   * set callback on pointer moved to fix issue of no clicking when not in focus
   */
  _setPointerMovedCallback() {
    this._onPointerMoved = () => {
      this.update(false);
    };
    game.input.addMoveCallback(this._onPointerMoved, this);
  }

  /**
   * delete the pointer moved callback
   */
  _deletePointerMovedCallback() {
    game.input.deleteMoveCallback(this._onPointerMoved, this);
  }

  /**
   * true if the scrollBar is currently visible
   * @returns {boolean}
   */
  get isScrollBarVisible() {
    return this._scrollBar.visible;
  }

  /**
   * toggle auto resizing of the scroll area
   * @param {boolean} value
   */
  set autoResize(value) {
    this._autoResize = value;
  }

  /**
   * enable the scrolling
   */
  enable() {
    this._enabled = true;
  }

  /**
   * disable the scrolling
   */
  disable() {
    this._enabled = false;
  }

  /**
   * destruction method
   */
  destroy() {
    this._deletePointerMovedCallback();
    this.update = () => {};
    this._content.removeChildren();
    this._childrenWithInput.length = 0;
    this._scrollMask.destroy();
    if (this._scrollBar.destroy) {
      this._scrollBar.destroy();
    }
    super.destroy();
  }
}
